import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import planoService, {
  type PlanoTrabalhoAnaliseJuridica,
  type PlanoTrabalhoAnaliseJuridicaCatalogo,
  type PlanoTrabalhoAnexo,
  type PlanoTrabalhoParecerAnexo,
} from '../services/planoTrabalhoService';
import { useAuth } from '../context/AuthContext';
import { UserRole } from '../types/user.types';
import FileViewerModal from '../components/FileViewerModal';
import AuthenticatedFileButton from '../components/AuthenticatedFileButton';
import type { PlanoTrabalho } from '../types/planoTrabalho.types';
import Toast from '../components/Toast';
import documentoInstitucionalService, { type DocumentoInstitucional } from '../services/documentoInstitucionalService';
import { TIPOS_DOCUMENTO_LABELS, TIPOS_DECLARACAO, type TipoDocumentoInstitucional, type StatusDocumento, STATUS_DOCUMENTO_LABELS } from '../types/documentoInstitucional.types';
import documentoPessoalService, { type DocumentoPessoalDTO } from '../services/documentoPessoalService';
import dirigenteService from '../services/dirigenteService';
import type { Dirigente } from '../types/dirigente.types';
import { UI_THEME } from '../ui/theme';
import { toUpperText } from '../utils/formatters';

const TIPOS_DOCUMENTO_PESSOAL_LABELS: Record<string, string> = {
  DOCUMENTO_FOTO_DIRIGENTE: 'Documento de identificação com foto',
  CERTIDAO_TCE_DIRIGENTE: 'Certidão negativa do TCE',
  RG_DIRIGENTE: 'RG',
  CPF_DIRIGENTE: 'CPF',
  COMPROVANTE_ENDERECO_DIRIGENTE: 'Comprovante de endereço',
};

const TIPO_DOCUMENTO_LABELS_PLANO: Record<string, string> = {
  PLANO_ASSINADO_PRESIDENTE: 'Plano assinado pelo presidente',
  COMPROVANTE_CONTA_ZERADA: 'Comprovante de conta zerada',
  CONTRAPARTIDA: 'Contrapartida',
  ATA_COMISSAO: 'Ata da comissão',
  TERMO_FOMENTO: 'Termo de fomento',
};

const fDateShort = (v?: string) =>
  v ? new Date(v).toLocaleDateString('pt-BR') : '-';

const PROCURADORIA_LIST_ROUTE = '/dashboard/gestao/planos-trabalho?etapaAtual=PROCURADORIA_JURIDICA';

type DocViewer = {
  url: string;
  nome: string;
  mimeType?: string;
  groupLabel: string;
  documentCode?: string;
  globalIndex: number; // -1 for standalone viewers (parecer assinado)
};

const ENCAMINHAMENTOS: Array<{
  value: NonNullable<PlanoTrabalhoAnaliseJuridica['encaminhamentoFinal']>;
  label: string;
  description: string;
  activeClass: string;
}> = [
  {
    value: 'APROVACAO',
    label: 'Aprovação',
    description: 'Parceria em conformidade com a legislação.',
    activeClass: 'border-green-400 bg-green-50 text-green-800',
  },
  {
    value: 'APROVACAO_COM_RESSALVAS',
    label: 'Aprovação com ressalvas',
    description: 'Existem pendências saneáveis ou ressalvas formais.',
    activeClass: 'border-yellow-400 bg-yellow-50 text-yellow-800',
  },
  {
    value: 'REJEICAO',
    label: 'Rejeição',
    description: 'Foram identificadas irregularidades graves ou impeditivas.',
    activeClass: 'border-red-400 bg-red-50 text-red-800',
  },
];

const ETAPA_LABELS: Record<string, string> = {
  CONVENIOS_ANALISE: 'Convênios — análise inicial',
  GESTOR_PARCERIA: 'Gestor da parceria',
  COMISSAO_SELECAO: 'Comissão de seleção',
  CONVENIOS_MINUTA: 'Convênios — minuta do termo de fomento',
  PROCURADORIA_JURIDICA: 'Procuradoria Geral Municipal',
  GESTOR_JURIDICO_REVISAO: 'Revisão do Gestor Jurídico',
  CONVENIOS_FORMALIZACAO: 'Convênios — formalização do termo de fomento',
  CONCLUIDO: 'Concluído',
};

function DocumentValidityBadge({ dataValidade }: { dataValidade?: string | null }) {
  if (!dataValidade) return null;

  const [year, month, day] = dataValidade.split('T')[0].split('-').map(Number);
  const validade = new Date(Date.UTC(year, month - 1, day));
  const now = new Date();
  const todayUTC = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
  const diffDays = Math.round((validade.getTime() - todayUTC.getTime()) / 86_400_000);

  if (diffDays < 0) {
    return (
      <span className="inline-flex items-center rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-800">
        Vencido
      </span>
    );
  }

  if (diffDays <= 30) {
    return (
      <span className="inline-flex items-center rounded-full bg-yellow-100 px-2.5 py-0.5 text-xs font-medium text-yellow-800">
        Vence em {diffDays === 0 ? 'hoje' : `${diffDays} dia${diffDays === 1 ? '' : 's'}`}
      </span>
    );
  }

  return null;
}

const PlanoAnaliseJuridicaPage: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin, hasRole, user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deciding, setDeciding] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState<{ message: string; type?: 'success' | 'error' | 'info' } | null>(null);

  const [docsInstituicao, setDocsInstituicao] = useState<DocumentoInstitucional[]>([]);
  const [docsDirigentes, setDocsDirigentes] = useState<Array<{ dirigente: Dirigente; docs: DocumentoPessoalDTO[] }>>([]);
  const [docsPlano, setDocsPlano] = useState<PlanoTrabalhoAnexo[]>([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [docsError, setDocsError] = useState(false);

  const [viewer, setViewer] = useState<DocViewer | null>(null);

  const [parecerAnexo, setParecerAnexo] = useState<PlanoTrabalhoParecerAnexo | null>(null);
  const [gestorParecerAnexo, setGestorParecerAnexo] = useState<PlanoTrabalhoParecerAnexo | null>(null);
  const [downloadingParecerTipo, setDownloadingParecerTipo] = useState<string | null>(null);
  const [gerandoPdfTipo, setGerandoPdfTipo] = useState<string | null>(null);
  const [downloadingZip, setDownloadingZip] = useState(false);
  const [uploadingParecer, setUploadingParecer] = useState(false);
  const parecerInputRef = useRef<HTMLInputElement>(null);

  const [plano, setPlano] = useState<PlanoTrabalho | null>(null);
  const [catalogo, setCatalogo] = useState<PlanoTrabalhoAnaliseJuridicaCatalogo | null>(null);
  const [encaminhamentoFinal, setEncaminhamentoFinal] = useState<PlanoTrabalhoAnaliseJuridica['encaminhamentoFinal']>();
  const [fundamentacao, setFundamentacao] = useState('');
  const [numeroParecer, setNumeroParecer] = useState('');
  const [anoParecer, setAnoParecer] = useState('');
  const [lastSavedAt, setLastSavedAt] = useState('');
  const [enviadoAprovacaoEm, setEnviadoAprovacaoEm] = useState<string | null>(null);
  const [enviadoAprovacaoPor, setEnviadoAprovacaoPor] = useState<string | null>(null);
  const [copiedIds, setCopiedIds] = useState<Record<string, boolean>>({});
  const [expandedPareceres, setExpandedPareceres] = useState<Record<string, boolean>>({});
  const isSaas = UI_THEME === 'saas';
  const accentTextClass = isSaas ? 'text-teal-700 dark:text-teal-300' : 'text-blue-700 dark:text-blue-300';
  const headerClass = isSaas
    ? 'border-teal-100 bg-gradient-to-br from-white via-teal-50/70 to-indigo-50 shadow-sm dark:border-slate-700 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800'
    : 'border-slate-200 bg-gradient-to-br from-white via-slate-50 to-blue-50 shadow-sm dark:border-slate-700 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800';
  const panelClass = 'rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900/80';
  const panelHeaderClass = 'border-b border-slate-200 px-4 py-3 dark:border-slate-700';
  const labelClass = 'mb-1 block text-sm font-medium text-slate-700 dark:text-slate-200';
  const inputClass = 'block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 shadow-sm focus:border-blue-500 focus:ring-blue-500 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:placeholder:text-slate-500 dark:disabled:bg-slate-800 dark:disabled:text-slate-500';
  const primaryButtonClass = isSaas
    ? 'inline-flex items-center rounded-lg bg-teal-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-teal-700 disabled:cursor-not-allowed disabled:opacity-50'
    : 'inline-flex items-center rounded-lg bg-blue-700 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-50';
  const successButtonClass = 'inline-flex items-center rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50';
  const secondaryButtonClass = 'inline-flex items-center rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800';
  const linkButtonClass = isSaas
    ? 'text-xs font-semibold text-teal-700 hover:text-teal-800 hover:underline dark:text-teal-300 dark:hover:text-teal-200'
    : 'text-xs font-semibold text-blue-700 hover:text-blue-800 hover:underline dark:text-blue-300 dark:hover:text-blue-200';
  const documentPanelClass = '[&_h3]:text-slate-700 dark:[&_h3]:text-slate-200 [&_thead]:bg-slate-50 dark:[&_thead]:bg-slate-800/70 [&_th]:text-slate-700 dark:[&_th]:text-slate-200 [&_tr]:border-slate-200 dark:[&_tr]:border-slate-700 [&_td]:text-slate-600 dark:[&_td]:text-slate-300 [&_tbody_tr:hover]:bg-slate-50 dark:[&_tbody_tr:hover]:bg-slate-800/60';
  const mutedTextClass = 'text-slate-500 dark:text-slate-400';
  const [showRetificacaoModal, setShowRetificacaoModal] = useState(false);
  const [retificacaoMotivo, setRetificacaoMotivo] = useState('');
  const [solicitandoRetificacao, setSolicitandoRetificacao] = useState(false);

  const copyToClipboard = async (text: string) => {
    if (navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(text);
        return true;
      } catch {
        // Some embedded browsers block navigator.clipboard even on localhost.
      }
    }

    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.left = '-9999px';
    textarea.style.top = '0';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();

    try {
      return document.execCommand('copy');
    } finally {
      document.body.removeChild(textarea);
    }
  };

  const handleCopyId = async (docId: string, code: string) => {
    if (await copyToClipboard(code)) {
      setCopiedIds(prev => ({ ...prev, [docId]: true }));
      setTimeout(() => setCopiedIds(prev => ({ ...prev, [docId]: false })), 2000);
    } else {
      setToast({ message: 'Nao foi possivel copiar para o clipboard.', type: 'error' });
    }
  };

  const allDocsForCopy = useMemo(() => {
    const list: Array<{ id: string; code?: string | null; label: string }> = [];
    docsInstituicao
      .filter(d => d.id && !TIPOS_DECLARACAO.includes(d.tipoDocumento as TipoDocumentoInstitucional))
      .forEach(d => list.push({ id: d.id!, code: d.documentCode, label: TIPOS_DOCUMENTO_LABELS[d.tipoDocumento as TipoDocumentoInstitucional] || d.tipoDocumento }));
    docsInstituicao
      .filter(d => d.id && TIPOS_DECLARACAO.includes(d.tipoDocumento as TipoDocumentoInstitucional))
      .forEach(d => list.push({ id: d.id!, code: d.documentCode, label: TIPOS_DOCUMENTO_LABELS[d.tipoDocumento as TipoDocumentoInstitucional] || d.tipoDocumento }));
    docsDirigentes.forEach(({ docs }) =>
      docs.filter(d => d.id).forEach(d => list.push({ id: d.id!, code: d.documentCode, label: TIPOS_DOCUMENTO_PESSOAL_LABELS[d.tipoDocumento] || d.tipoDocumento }))
    );
    docsPlano.filter(a => a.id).forEach(a => list.push({ id: a.id!, code: a.documentCode, label: TIPO_DOCUMENTO_LABELS_PLANO[a.tipoDocumento] || a.tipoDocumento }));
    return list;
  }, [docsInstituicao, docsDirigentes, docsPlano]);

  const handleCopyAllIds = async (docs: Array<{ id: string; code?: string | null; label: string }>) => {
    const lines = docs
      .filter(d => d.code)
      .map(d => `${d.code}  ${d.label}`)
      .join('\n');
    if (!lines) {
      setToast({ message: 'Nenhum ID disponivel para copiar.', type: 'info' });
      return;
    }
    try {
      const copied = await copyToClipboard(lines);
      if (!copied) throw new Error('copy failed');
      setToast({ message: 'ids copiados para clipboard', type: 'success' });
    } catch {
      setToast({ message: 'Nao foi possivel copiar para o clipboard.', type: 'error' });
    }
  };

  useEffect(() => {
    let active = true;
    const load = async () => {
      if (!id) {
        setError('Plano de trabalho não informado.');
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        setError('');
        const planoData = await planoService.get(id);
        const catalogoData = await planoService.getAnaliseJuridicaCatalogo('TERMO_FOMENTO');
        const analiseData = await planoService.getAnaliseJuridica(id).catch((err: any) => {
          if (err?.response?.status === 404) return null;
          throw err;
        });
        if (!active) return;
        setPlano(planoData);
        setCatalogo(catalogoData);
        if (analiseData) {
          setEncaminhamentoFinal(analiseData.encaminhamentoFinal);
          setFundamentacao(analiseData.fundamentacao || '');
          setNumeroParecer(analiseData.numeroParecer || '');
          setAnoParecer(analiseData.anoParecer != null ? String(analiseData.anoParecer) : '');
          setLastSavedAt(analiseData.dataAtualizacao || '');
          setEnviadoAprovacaoEm(analiseData.enviadoAprovacaoEm || null);
          setEnviadoAprovacaoPor(analiseData.enviadoAprovacaoPor || null);
        }
      } catch (err: any) {
        if (!active) return;
        setError(err?.response?.data?.error || 'Não foi possível carregar a análise jurídica.');
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => { active = false; };
  }, [id]);

  useEffect(() => {
    if (!plano || !id) return;
    let active = true;
    const loadDocs = async () => {
      setDocsLoading(true);
      setDocsError(false);
      try {
        const [anexos, parecerData, gestorParecerData] = await Promise.all([
          planoService.listAnexos(id),
          planoService.getParecerAnexo(id).catch(() => null),
          planoService.getParecerAnexo(id, 'GESTOR_PARCERIA').catch(() => null),
        ]);
        if (!active) return;
        setDocsPlano(anexos);
        setParecerAnexo(parecerData);
        setGestorParecerAnexo(gestorParecerData);
        if (plano.instituicaoId) {
          const [docs, dirigentes] = await Promise.all([
            documentoInstitucionalService.listar(plano.instituicaoId).catch(() => [] as DocumentoInstitucional[]),
            dirigenteService.listar(plano.instituicaoId, true).catch(() => [] as Dirigente[]),
          ]);
          if (!active) return;
          setDocsInstituicao(docs.filter((d) => d.statusDocumento !== 'SUBSTITUIDO'));
          const dirComDocs = await Promise.all(
            dirigentes.map(async (dirigente) => ({
              dirigente,
              docs: await documentoPessoalService
                .listarPorDirigente(String(dirigente.pessoaId || dirigente.id || ''))
                .catch(() => [] as DocumentoPessoalDTO[]),
            }))
          );
          if (!active) return;
          setDocsDirigentes(dirComDocs);
        }
      } catch {
        if (active) setDocsError(true);
      } finally {
        if (active) setDocsLoading(false);
      }
    };
    loadDocs();
    return () => { active = false; };
  }, [plano, id]);

  const isJuridico = hasRole(UserRole.JURIDICO);
  const isAuxiliar = hasRole(UserRole.JURIDICO_AUXILIAR);
  const isAtEtapaJuridica = plano?.etapaAtual === 'PROCURADORIA_JURIDICA';
  // Elaboração do parecer: procurador, auxiliar designado e admin podem editar/salvar/anexar.
  const canEditParecer = Boolean(
    plano &&
    plano.modalidade === 'TERMO_FOMENTO' &&
    plano.etapaAtual === 'PROCURADORIA_JURIDICA' &&
    (isAdmin() || isJuridico || isAuxiliar)
  );
  // Conclusão da etapa (avançar/reprovar): apenas procurador e admin — o auxiliar NÃO conclui.
  const canEditConclusao = Boolean(
    plano &&
    plano.modalidade === 'TERMO_FOMENTO' &&
    plano.etapaAtual === 'PROCURADORIA_JURIDICA' &&
    (isAdmin() || isJuridico)
  );

  // Combined docs for global viewer navigation
  const combinedDocs = useMemo(() => {
    const docs: Array<{ id: string; url: string; nome: string; mimeType?: string; groupLabel: string; documentCode?: string }> = [];
    docsInstituicao
      .filter((d) => !TIPOS_DECLARACAO.includes(d.tipoDocumento as TipoDocumentoInstitucional))
      .forEach((d) => docs.push({
        id: d.id,
        url: documentoInstitucionalService.viewUrl(d.id),
        nome: TIPOS_DOCUMENTO_LABELS[d.tipoDocumento as TipoDocumentoInstitucional] || d.nomeOriginal,
        mimeType: d.tipoMime,
        groupLabel: 'Documentos da instituição',
        documentCode: d.documentCode,
      }));
    docsInstituicao
      .filter((d) => TIPOS_DECLARACAO.includes(d.tipoDocumento as TipoDocumentoInstitucional))
      .forEach((d) => docs.push({
        id: d.id,
        url: documentoInstitucionalService.viewUrl(d.id),
        nome: TIPOS_DOCUMENTO_LABELS[d.tipoDocumento as TipoDocumentoInstitucional] || d.nomeOriginal,
        mimeType: d.tipoMime,
        groupLabel: 'Declarações',
        documentCode: d.documentCode,
      }));
    docsDirigentes.forEach(({ dirigente, docs: dDocs }) =>
      dDocs.forEach((d) => docs.push({
        id: d.id,
        url: documentoPessoalService.viewUrl(d.id),
        nome: TIPOS_DOCUMENTO_PESSOAL_LABELS[d.tipoDocumento] || d.nomeOriginal,
        mimeType: d.tipoMime,
        groupLabel: dirigente.nomeCompleto,
        documentCode: d.documentCode,
      }))
    );
    docsPlano
      .filter((a) => a.viewUrl)
      .forEach((a) => docs.push({
        id: a.id,
        url: `/api${a.viewUrl}`,
        nome: a.nomeOriginal,
        mimeType: a.mimeType,
        groupLabel: 'Documentos do plano de trabalho',
        documentCode: a.documentCode,
      }));
    return docs;
  }, [docsInstituicao, docsDirigentes, docsPlano]);

  const docIndexMap = useMemo(() => {
    const map: Record<string, number> = {};
    combinedDocs.forEach((d, i) => { map[d.id] = i; });
    return map;
  }, [combinedDocs]);

  const displayDocumentCode = (code?: string | null) => {
    const normalized = code?.trim();
    return normalized || 'Sem ID';
  };

  const openViewer = (globalIndex: number) => {
    const doc = combinedDocs[globalIndex];
    if (!doc) return;
    setViewer({ url: doc.url, nome: doc.nome, mimeType: doc.mimeType, groupLabel: doc.groupLabel, documentCode: doc.documentCode, globalIndex });
  };

  const handleDownloadParecerAnexo = async (tipo: string, nomeOriginal: string) => {
    if (!id) return;
    setDownloadingParecerTipo(tipo);
    try {
      const blob = await planoService.downloadParecerAnexo(id, tipo);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = nomeOriginal;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setToast({ message: 'Não foi possível baixar o parecer.', type: 'error' });
    } finally {
      setDownloadingParecerTipo(null);
    }
  };

  const handleDownloadZip = async () => {
    if (!id || !plano) return;
    setDownloadingZip(true);
    try {
      const blob = await planoService.downloadTodosDocumentosZip(id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `plano-${id.substring(0, 8)}-documentos.zip`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      let message = 'Não foi possível gerar o ZIP dos documentos.';
      const responseData = err?.response?.data;
      if (responseData instanceof Blob) {
        const errorText = await responseData.text();
        if (errorText) {
          try {
            const parsed = JSON.parse(errorText);
            message = parsed?.error || message;
          } catch {
            message = errorText;
          }
        }
      } else if (responseData?.error) {
        message = responseData.error;
      }
      setToast({ message, type: 'error' });
    } finally {
      setDownloadingZip(false);
    }
  };

  const handleGerarPdfParecer = async (tipo: string) => {
    if (!id) return;
    setGerandoPdfTipo(tipo);
    try {
      const novo = await planoService.gerarPdfParecer(id, tipo);
      if (tipo === 'GESTOR_PARCERIA') setGestorParecerAnexo(novo);
      else setParecerAnexo(novo);
      setToast({ message: 'PDF gerado com sucesso.', type: 'success' });
    } catch {
      setToast({ message: 'Não foi possível gerar o PDF do parecer.', type: 'error' });
    } finally {
      setGerandoPdfTipo(null);
    }
  };

  const handleParecerUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !id) return;
    if (file.type !== 'application/pdf') {
      setToast({ message: 'Apenas arquivos PDF são aceitos para o parecer assinado.', type: 'error' });
      return;
    }
    try {
      setUploadingParecer(true);
      const result = await planoService.uploadParecerAnexo(id, file);
      setParecerAnexo(result);
      setToast({ message: 'Parecer assinado anexado com sucesso.', type: 'success' });
    } catch (err: any) {
      setToast({ message: err?.response?.data?.error || 'Não foi possível anexar o parecer.', type: 'error' });
    } finally {
      setUploadingParecer(false);
      if (parecerInputRef.current) parecerInputRef.current.value = '';
    }
  };

  const handleSave = async (): Promise<boolean> => {
    if (!id || !catalogo) return false;
    try {
      setSaving(true);
      const anoParecerNumero = anoParecer.trim() ? Number(anoParecer.trim()) : undefined;
      const saved = await planoService.saveAnaliseJuridica(id, {
        checklistVersao: catalogo.versao,
        encaminhamentoFinal,
        fundamentacao,
        parecerConsolidado: '',
        numeroParecer: numeroParecer.trim() || undefined,
        anoParecer: anoParecerNumero != null && !Number.isNaN(anoParecerNumero) ? anoParecerNumero : undefined,
        fases: [],
        itens: [],
      });
      setLastSavedAt(saved.dataAtualizacao || '');
      // O backend limpa o envio para aprovação a cada salvamento (exige reenvio); reflete isso na UI.
      setEnviadoAprovacaoEm(saved.enviadoAprovacaoEm || null);
      setEnviadoAprovacaoPor(saved.enviadoAprovacaoPor || null);
      setToast({ message: 'Análise jurídica salva com sucesso.', type: 'success' });
      return true;
    } catch (err: any) {
      setToast({ message: err?.response?.data?.error || 'Não foi possível salvar a análise jurídica.', type: 'error' });
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleEnviarAprovacao = async () => {
    if (!id || !catalogo || !encaminhamentoFinal) {
      setToast({ message: 'Selecione um encaminhamento jurídico antes de enviar para aprovação.', type: 'error' });
      return;
    }
    try {
      setDeciding(true);
      const ok = await handleSave();
      if (!ok) return;
      const result = await planoService.enviarAnaliseParaAprovacao(id);
      setEnviadoAprovacaoEm(result.enviadoAprovacaoEm || null);
      setEnviadoAprovacaoPor(result.enviadoAprovacaoPor || null);
      setToast({ message: 'Parecer enviado para aprovação do procurador supervisor.', type: 'success' });
    } catch (err: any) {
      setToast({ message: err?.response?.data?.error || 'Não foi possível enviar o parecer para aprovação.', type: 'error' });
    } finally {
      setDeciding(false);
    }
  };

  const handleConcluirEtapa = async () => {
    if (!id || !catalogo || !encaminhamentoFinal) {
      setToast({ message: 'Selecione um encaminhamento jurídico antes de concluir a etapa.', type: 'error' });
      return;
    }
    if (!parecerAnexo) {
      setToast({ message: 'Anexe o parecer jurídico assinado (PDF) antes de concluir a etapa.', type: 'error' });
      return;
    }
    if (!numeroParecer.trim() || !anoParecer.trim()) {
      setToast({ message: 'Informe o número e o ano do parecer antes de concluir a etapa.', type: 'error' });
      return;
    }
    try {
      setDeciding(true);
      // Se o salvamento falhar, aborta para nao avancar a etapa sem persistir
      // o parecer (numero/ano, fundamentacao, encaminhamento).
      const saved = await handleSave();
      if (!saved) return;
      const fundamentacaoNormalizada = fundamentacao.trim();
      const payload = { motivo: fundamentacaoNormalizada };
      if (encaminhamentoFinal === 'APROVACAO' || encaminhamentoFinal === 'APROVACAO_COM_RESSALVAS') {
        await planoService.aprovar(id, payload);
      } else {
        await planoService.reprovar(id, payload);
      }
      setToast({ message: 'Decisão jurídica registrada com sucesso.', type: 'success' });
      window.setTimeout(() => navigate(`/dashboard/plano/full/${id}?section=geral&focus=juridico-review`), 700);
    } catch (err: any) {
      setToast({ message: err?.response?.data?.error || 'Não foi possível concluir a etapa jurídica.', type: 'error' });
    } finally {
      setDeciding(false);
    }
  };

  const handleSolicitarRetificacao = async () => {
    if (!id || !retificacaoMotivo.trim()) {
      setToast({ message: 'Descreva o que precisa ser retificado.', type: 'error' });
      return;
    }
    try {
      setSolicitandoRetificacao(true);
      const updated = await planoService.solicitarRetificacao(id, retificacaoMotivo.trim(), 'PROCURADORIA_JURIDICA');
      setPlano(updated);
      setShowRetificacaoModal(false);
      setRetificacaoMotivo('');
      setToast({ message: 'Retificação solicitada. O operador será notificado.', type: 'success' });
    } catch (err: any) {
      setToast({ message: err?.response?.data?.error || 'Não foi possível solicitar a retificação.', type: 'error' });
    } finally {
      setSolicitandoRetificacao(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className={`h-8 w-8 animate-spin rounded-full border-b-2 ${isSaas ? 'border-teal-600' : 'border-blue-700'}`} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-900/60 dark:bg-red-950/30 dark:text-red-200">
        {error}
      </div>
    );
  }

  if (!plano || !catalogo) {
    return (
      <div className={`${panelClass} p-6 text-center text-sm ${mutedTextClass}`}>
        Não foi possível montar a tela da análise jurídica.
      </div>
    );
  }

  return (
    <div className="w-full space-y-6">
      <div className={`rounded-2xl border p-5 ${headerClass}`}>
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <p className={`mb-1 text-xs font-semibold uppercase tracking-[0.22em] ${accentTextClass}`}>
            Análise jurídica - Procuradoria Geral Municipal
          </p>
          <h1 className="text-2xl font-bold text-slate-950 dark:text-slate-50">
            {plano.titulo || 'Plano de Trabalho'}
          </h1>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-xs font-medium">
            {plano.numeroProtocolo && (
              <span className="rounded-full border border-slate-200 bg-white/80 px-3 py-1 text-slate-700 dark:border-slate-700 dark:bg-slate-950/70 dark:text-slate-200">Protocolo: {plano.numeroProtocolo}</span>
            )}
            <span className="rounded-full border border-slate-200 bg-white/80 px-3 py-1 text-slate-700 dark:border-slate-700 dark:bg-slate-950/70 dark:text-slate-200">
              Etapa: {ETAPA_LABELS[plano.etapaAtual || ''] || plano.etapaAtual || '—'}
            </span>
            {lastSavedAt && (
              <span className="rounded-full border border-slate-200 bg-white/80 px-3 py-1 text-slate-500 dark:border-slate-700 dark:bg-slate-950/70 dark:text-slate-300">
                Salvo: {new Date(lastSavedAt).toLocaleString('pt-BR')}
              </span>
            )}
          </div>
        </div>

        <div className="flex flex-wrap gap-2 shrink-0">
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || deciding || !canEditParecer}
            className={primaryButtonClass}
          >
            {saving ? 'Salvando...' : 'Salvar rascunho'}
          </button>
          <button
            type="button"
            onClick={() => navigate(`/dashboard/plano/full/${plano.id}`)}
            className={secondaryButtonClass}
          >
            Ver plano
          </button>
          <button
            type="button"
            onClick={() => navigate(PROCURADORIA_LIST_ROUTE)}
            className={secondaryButtonClass}
          >
            Voltar
          </button>
        </div>
      </div>
      </div>

      {!canEditParecer && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-200">
          Esta análise está disponível para consulta. A edição requer o plano em modalidade Termo de Fomento na etapa da Procuradoria Geral Municipal.
        </div>
      )}

      {enviadoAprovacaoEm && (
        <div className="rounded-2xl border border-indigo-200 bg-indigo-50 p-3 text-sm text-indigo-800 dark:border-indigo-900/60 dark:bg-indigo-950/30 dark:text-indigo-200">
          Parecer enviado para aprovação
          {plano?.procuradorJuridicoNome ? ` do procurador ${plano.procuradorJuridicoNome}` : ' do procurador supervisor'}
          {enviadoAprovacaoPor ? ` por ${enviadoAprovacaoPor}` : ''} em{' '}
          {new Date(enviadoAprovacaoEm).toLocaleString('pt-BR')}.
          {(isJuridico || isAdmin()) && ' Revise e conclua a etapa abaixo.'}
        </div>
      )}

      <div className={`${panelClass} ${documentPanelClass} overflow-hidden`}>
        <div className={`${panelHeaderClass} flex items-center justify-between`}>
          <div>
            <h2 className="text-base font-semibold text-slate-950 dark:text-slate-50">Documentos do processo</h2>
            <p className={`mt-0.5 text-xs ${mutedTextClass}`}>
              Instituição, dirigentes e plano de trabalho
              {combinedDocs.length > 0 && ` - ${combinedDocs.length} documento${combinedDocs.length !== 1 ? 's' : ''}`}
            </p>
          </div>
          {allDocsForCopy.some(d => d.code) && (
            <button
              type="button"
              onClick={() => handleCopyAllIds(allDocsForCopy)}
              className={linkButtonClass}
            >
              Copiar todos os IDs
            </button>
          )}
        </div>

        {docsLoading && (
          <div className="flex items-center justify-center py-8">
            <div className={`h-6 w-6 animate-spin rounded-full border-b-2 ${isSaas ? 'border-teal-600' : 'border-blue-700'}`} />
          </div>
        )}

        {!docsLoading && docsError && (
          <div className="m-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900/60 dark:bg-red-950/30 dark:text-red-200">
            Não foi possível carregar os documentos. Tente recarregar a página.
          </div>
        )}

        {!docsLoading && !docsError && (
          <div className="p-4 space-y-6">

            {/* Documentos da instituição */}
            {(() => {
              const docs = docsInstituicao.filter((d) => !TIPOS_DECLARACAO.includes(d.tipoDocumento as TipoDocumentoInstitucional));
              return (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-semibold text-gray-700">Documentos da instituição</h3>
                    {docs.some(d => d.documentCode) && (
                      <button
                        type="button"
                        onClick={() => handleCopyAllIds(docs.map(d => ({ id: d.id, code: d.documentCode, label: TIPOS_DOCUMENTO_LABELS[d.tipoDocumento as TipoDocumentoInstitucional] || d.tipoDocumento })))}
                        className="text-xs text-gray-500 hover:text-gray-700 underline"
                      >
                        Copiar todos os IDs
                      </button>
                    )}
                  </div>
                  {docs.length === 0 ? (
                    <p className="text-sm text-gray-500">Nenhum documento institucional encontrado.</p>
                  ) : (
                    <div className="overflow-x-auto border border-gray-200 rounded-lg">
                      <table className="min-w-full text-sm">
                        <thead className="bg-gray-50 border-b border-gray-200">
                          <tr>
                            <th className="text-left font-semibold text-gray-700 px-4 py-2.5">ID</th>
                            <th className="text-left font-semibold text-gray-700 px-4 py-2.5">Documento</th>
                            <th className="text-left font-semibold text-gray-700 px-4 py-2.5">Arquivo</th>
                            <th className="text-left font-semibold text-gray-700 px-4 py-2.5">Status</th>
                            <th className="text-left font-semibold text-gray-700 px-4 py-2.5">Validade</th>
                            <th className="text-left font-semibold text-gray-700 px-4 py-2.5">Número</th>
                            <th className="px-4 py-2.5" />
                          </tr>
                        </thead>
                        <tbody>
                          {docs.map((d) => {
                            const code = d.documentCode;
                            const displayLabel = displayDocumentCode(code);
                            return (
                            <tr key={d.id} className="border-b last:border-b-0 hover:bg-gray-50">
                              <td className="px-4 py-3 whitespace-nowrap">
                                <div className="flex items-center gap-1.5">
                                  <span className="font-mono text-xs text-gray-500">{displayLabel}</span>
                                  {code && (
                                    <button
                                      type="button"
                                      aria-label="Copiar ID"
                                      onClick={() => handleCopyId(d.id, code)}
                                      className="text-gray-400 hover:text-gray-600"
                                    >
                                      {copiedIds[d.id] ? (
                                        <svg className="h-3.5 w-3.5 text-green-500" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                                      ) : (
                                        <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor"><path d="M8 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z" /><path d="M6 3a2 2 0 00-2 2v11a2 2 0 002 2h8a2 2 0 002-2V5a2 2 0 00-2-2 3 3 0 01-3 3H9a3 3 0 01-3-3z" /></svg>
                                      )}
                                    </button>
                                  )}
                                </div>
                              </td>
                              <td className="px-4 py-3 font-medium text-gray-900">
                                {TIPOS_DOCUMENTO_LABELS[d.tipoDocumento as TipoDocumentoInstitucional] || d.tipoDocumento}
                              </td>
                              <td className="px-4 py-3 text-gray-600 max-w-[180px] truncate">{d.nomeOriginal}</td>
                              <td className="px-4 py-3 text-gray-600">
                                {STATUS_DOCUMENTO_LABELS[d.statusDocumento as StatusDocumento] || d.statusDocumento}
                              </td>
                              <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                                <span>{d.dataValidade ? fDateShort(d.dataValidade) : '—'}</span>
                                {d.dataValidade && <DocumentValidityBadge dataValidade={d.dataValidade} />}
                              </td>
                              <td className="px-4 py-3 text-gray-600">{d.numeroDocumento || '—'}</td>
                              <td className="px-4 py-3">
                                <button
                                  type="button"
                                  onClick={() => openViewer(docIndexMap[d.id])}
                                  className="text-xs font-medium text-blue-600 hover:text-blue-700 hover:underline"
                                >
                                  Visualizar
                                </button>
                              </td>
                            </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Declarações */}
            {(() => {
              const docs = docsInstituicao.filter((d) => TIPOS_DECLARACAO.includes(d.tipoDocumento as TipoDocumentoInstitucional));
              return (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Declarações</h3>
                  {docs.length === 0 ? (
                    <p className="text-sm text-gray-500">Nenhuma declaração encontrada.</p>
                  ) : (
                    <div className="overflow-x-auto border border-gray-200 rounded-lg">
                      <table className="min-w-full text-sm">
                        <thead className="bg-gray-50 border-b border-gray-200">
                          <tr>
                            <th className="text-left font-semibold text-gray-700 px-4 py-2.5">ID</th>
                            <th className="text-left font-semibold text-gray-700 px-4 py-2.5">Documento</th>
                            <th className="text-left font-semibold text-gray-700 px-4 py-2.5">Arquivo</th>
                            <th className="text-left font-semibold text-gray-700 px-4 py-2.5">Status</th>
                            <th className="text-left font-semibold text-gray-700 px-4 py-2.5">Validade</th>
                            <th className="px-4 py-2.5" />
                          </tr>
                        </thead>
                        <tbody>
                          {docs.map((d) => (
                            <tr key={d.id} className="border-b last:border-b-0 hover:bg-gray-50">
                              <td className="px-4 py-3 font-mono text-xs text-gray-500 whitespace-nowrap">{displayDocumentCode(d.documentCode)}</td>
                              <td className="px-4 py-3 font-medium text-gray-900">
                                {TIPOS_DOCUMENTO_LABELS[d.tipoDocumento as TipoDocumentoInstitucional] || d.tipoDocumento}
                              </td>
                              <td className="px-4 py-3 text-gray-600 max-w-[180px] truncate">{d.nomeOriginal}</td>
                              <td className="px-4 py-3 text-gray-600">
                                {STATUS_DOCUMENTO_LABELS[d.statusDocumento as StatusDocumento] || d.statusDocumento}
                              </td>
                              <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                                <span>{d.dataValidade ? fDateShort(d.dataValidade) : '—'}</span>
                                {d.dataValidade && <DocumentValidityBadge dataValidade={d.dataValidade} />}
                              </td>
                              <td className="px-4 py-3">
                                <button
                                  type="button"
                                  onClick={() => openViewer(docIndexMap[d.id])}
                                  className="text-xs font-medium text-blue-600 hover:text-blue-700 hover:underline"
                                >
                                  Visualizar
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Documentos dos dirigentes */}
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Documentos dos dirigentes</h3>
              {docsDirigentes.length === 0 ? (
                <p className="text-sm text-gray-500">Nenhum dirigente ou documento encontrado.</p>
              ) : (
                <div className="space-y-4">
                  {docsDirigentes.map(({ dirigente, docs }) => (
                    <div key={String(dirigente.pessoaId || dirigente.id)}>
                      <p className="text-xs font-semibold text-gray-600 mb-1.5">
                        {dirigente.nomeCompleto}{dirigente.cargo ? ` — ${dirigente.cargo}` : ''}
                      </p>
                      {docs.length === 0 ? (
                        <p className="text-sm text-gray-500 pl-2">Sem documentos.</p>
                      ) : (
                        <div className="overflow-x-auto border border-gray-200 rounded-lg">
                          <table className="min-w-full text-sm">
                            <thead className="bg-gray-50 border-b border-gray-200">
                              <tr>
                                <th className="text-left font-semibold text-gray-700 px-4 py-2.5">ID</th>
                                <th className="text-left font-semibold text-gray-700 px-4 py-2.5">Documento</th>
                                <th className="text-left font-semibold text-gray-700 px-4 py-2.5">Arquivo</th>
                                <th className="text-left font-semibold text-gray-700 px-4 py-2.5">Status</th>
                                <th className="text-left font-semibold text-gray-700 px-4 py-2.5">Validade</th>
                                <th className="text-left font-semibold text-gray-700 px-4 py-2.5">Número</th>
                                <th className="px-4 py-2.5" />
                              </tr>
                            </thead>
                            <tbody>
                              {docs.map((d) => {
                                const code = d.documentCode;
                                return (
                                <tr key={d.id} className="border-b last:border-b-0 hover:bg-gray-50">
                                  <td className="px-4 py-3 whitespace-nowrap">
                                    <div className="flex items-center gap-1.5">
                                      <span className="font-mono text-xs text-gray-500">{displayDocumentCode(code)}</span>
                                      {code && (
                                        <button type="button" aria-label="Copiar ID" onClick={() => handleCopyId(d.id, code)} className="text-gray-400 hover:text-gray-600">
                                          {copiedIds[d.id] ? <svg className="h-3.5 w-3.5 text-green-500" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg> : <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor"><path d="M8 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z" /><path d="M6 3a2 2 0 00-2 2v11a2 2 0 002 2h8a2 2 0 002-2V5a2 2 0 00-2-2 3 3 0 01-3 3H9a3 3 0 01-3-3z" /></svg>}
                                        </button>
                                      )}
                                    </div>
                                  </td>
                                  <td className="px-4 py-3 font-medium text-gray-900">
                                    {TIPOS_DOCUMENTO_PESSOAL_LABELS[d.tipoDocumento] || d.tipoDocumento}
                                  </td>
                                  <td className="px-4 py-3 text-gray-600 max-w-[180px] truncate">{d.nomeOriginal}</td>
                                  <td className="px-4 py-3 text-gray-600">
                                    {STATUS_DOCUMENTO_LABELS[d.statusDocumento as StatusDocumento] || d.statusDocumento}
                                  </td>
                                  <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                                    <span>{d.dataValidade ? fDateShort(d.dataValidade) : '—'}</span>
                                    {d.dataValidade && <DocumentValidityBadge dataValidade={d.dataValidade} />}
                                  </td>
                                  <td className="px-4 py-3 text-gray-600">{d.numeroDocumento || '—'}</td>
                                  <td className="px-4 py-3">
                                    <button
                                      type="button"
                                      onClick={() => openViewer(docIndexMap[d.id])}
                                      className="text-xs font-medium text-blue-600 hover:text-blue-700 hover:underline"
                                    >
                                      Visualizar
                                    </button>
                                  </td>
                                </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Documentos do plano de trabalho + Pareceres do processo */}
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200">
                <h3 className="text-sm font-semibold text-gray-700">Documentos do plano de trabalho</h3>
                {docsPlano.some(a => a.documentCode) && (
                  <button
                    type="button"
                    onClick={() => handleCopyAllIds(docsPlano.map(a => ({ id: a.id, code: a.documentCode, label: TIPO_DOCUMENTO_LABELS_PLANO[a.tipoDocumento] || a.tipoDocumento })))}
                    className="text-xs text-gray-500 hover:text-gray-700 underline"
                  >
                    Copiar todos os IDs
                  </button>
                )}
              </div>
              {docsPlano.length === 0 ? (
                <p className="text-sm text-gray-500 px-4 py-3">Nenhum anexo no plano de trabalho.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="text-left font-semibold text-gray-700 px-4 py-2.5">ID</th>
                        <th className="text-left font-semibold text-gray-700 px-4 py-2.5">Documento</th>
                        <th className="text-left font-semibold text-gray-700 px-4 py-2.5">Arquivo</th>
                        <th className="text-left font-semibold text-gray-700 px-4 py-2.5">Etapa</th>
                        <th className="text-left font-semibold text-gray-700 px-4 py-2.5">Número</th>
                        <th className="px-4 py-2.5" />
                      </tr>
                    </thead>
                    <tbody>
                      {docsPlano.map((a) => {
                        const code = a.documentCode;
                        return (
                        <tr key={a.id} className="border-b last:border-b-0 hover:bg-gray-50">
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="flex items-center gap-1.5">
                              <span className="font-mono text-xs text-gray-500">{displayDocumentCode(code)}</span>
                              {code && (
                                <button type="button" aria-label="Copiar ID" onClick={() => handleCopyId(a.id, code)} className="text-gray-400 hover:text-gray-600">
                                  {copiedIds[a.id] ? <svg className="h-3.5 w-3.5 text-green-500" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg> : <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor"><path d="M8 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z" /><path d="M6 3a2 2 0 00-2 2v11a2 2 0 002 2h8a2 2 0 002-2V5a2 2 0 00-2-2 3 3 0 01-3 3H9a3 3 0 01-3-3z" /></svg>}
                                </button>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3 font-medium text-gray-900">
                            {TIPO_DOCUMENTO_LABELS_PLANO[a.tipoDocumento] || a.tipoDocumento}
                          </td>
                          <td className="px-4 py-3 text-gray-600 max-w-[180px] truncate">{a.nomeOriginal}</td>
                          <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                            {ETAPA_LABELS[a.etapa] || a.etapa}
                          </td>
                          <td className="px-4 py-3 text-gray-600">{a.documentoNumero || '—'}</td>
                          <td className="px-4 py-3">
                            {a.viewUrl && (
                              <button
                                type="button"
                                onClick={() => openViewer(docIndexMap[a.id])}
                                className="text-xs font-medium text-blue-600 hover:text-blue-700 hover:underline"
                              >
                                Visualizar
                              </button>
                            )}
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Pareceres do processo — dentro do mesmo box */}
              {(plano.parecerTecnico || plano.parecerJuridico || gestorParecerAnexo || parecerAnexo) && (() => {
                const itens: Array<{
                  key: string;
                  tipo: string;
                  label: string;
                  autor?: string | null;
                  texto?: string | null;
                  anexo?: PlanoTrabalhoParecerAnexo | null;
                }> = [];
                if (plano.parecerTecnico || gestorParecerAnexo) {
                  itens.push({
                    key: 'tecnico',
                    tipo: 'GESTOR_PARCERIA',
                    label: 'Parecer Técnico do Gestor da Parceria',
                    autor: plano.gestorNome,
                    texto: plano.parecerTecnico,
                    anexo: gestorParecerAnexo,
                  });
                }
                if (plano.parecerJuridico || parecerAnexo) {
                  itens.push({
                    key: 'juridico',
                    tipo: 'PROCURADORIA_JURIDICA',
                    label: 'Parecer Jurídico (PGM)',
                    autor: plano.procuradorJuridicoNome,
                    texto: plano.parecerJuridico,
                    anexo: parecerAnexo,
                  });
                }
                if (itens.length === 0) return null;
                return (
                  <>
                    <div className="border-t border-gray-200 px-4 py-3 bg-gray-50">
                      <h3 className="text-sm font-semibold text-gray-700">Pareceres do processo</h3>
                    </div>
                    <div className="divide-y divide-gray-100">
                    {itens.map((item) => {
                      const code = item.anexo?.documentCode;
                      const expanded = expandedPareceres[item.key] ?? false;
                      const longo = (item.texto?.trim().length ?? 0) > 250;
                      return (
                        <div key={item.key}>
                          {/* Cabeçalho: label + protocolo à esquerda, botão PDF à direita */}
                          <div className="flex items-start justify-between gap-4 px-4 py-3 bg-gray-50 border-b border-gray-100">
                            <div>
                              <span className="text-xs font-semibold uppercase tracking-wide text-blue-700">
                                {item.label}
                              </span>
                              {item.autor && (
                                <p className="text-xs text-gray-500 mt-0.5">{item.autor}</p>
                              )}
                              {code && (
                                <div className="flex items-center gap-1 mt-1">
                                  <span className="font-mono text-xs text-gray-500">{code}</span>
                                  <button
                                    type="button"
                                    aria-label="Copiar protocolo"
                                    onClick={() => handleCopyId(item.anexo!.id, code)}
                                    className="text-gray-400 hover:text-gray-600"
                                  >
                                    {copiedIds[item.anexo!.id] ? (
                                      <svg className="h-3.5 w-3.5 text-green-500" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                                    ) : (
                                      <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor"><path d="M8 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z" /><path d="M6 3a2 2 0 00-2 2v11a2 2 0 002 2h8a2 2 0 002-2V5a2 2 0 00-2-2 3 3 0 01-3 3H9a3 3 0 01-3-3z" /></svg>
                                    )}
                                  </button>
                                </div>
                              )}
                            </div>
                            <div className="flex items-center gap-3 shrink-0">
                              {item.anexo ? (
                                <button
                                  type="button"
                                  disabled={downloadingParecerTipo === item.tipo}
                                  onClick={() => handleDownloadParecerAnexo(item.tipo, item.anexo!.nomeOriginal)}
                                  className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700 hover:underline disabled:opacity-50"
                                >
                                  <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
                                  {downloadingParecerTipo === item.tipo ? 'Baixando…' : 'Baixar PDF'}
                                </button>
                              ) : item.texto ? (
                                <button
                                  type="button"
                                  disabled={gerandoPdfTipo === item.tipo}
                                  onClick={() => handleGerarPdfParecer(item.tipo)}
                                  className="inline-flex items-center gap-1 text-xs font-medium text-amber-600 hover:text-amber-700 hover:underline disabled:opacity-50"
                                >
                                  <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M6 2a2 2 0 00-2 2v12a2 2 0 002 2h8a2 2 0 002-2V7.414A2 2 0 0015.414 6L12 2.586A2 2 0 0010.586 2H6zm5 6a1 1 0 10-2 0v3.586l-1.293-1.293a1 1 0 10-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 11.586V8z" clipRule="evenodd" /></svg>
                                  {gerandoPdfTipo === item.tipo ? 'Gerando…' : 'Gerar PDF'}
                                </button>
                              ) : null}
                            </div>
                          </div>
                          {/* Corpo: texto */}
                          {item.texto ? (
                            <div className="px-4 py-3">
                              <p className={`text-sm text-gray-800 whitespace-pre-wrap leading-relaxed ${expanded ? '' : 'line-clamp-4'}`}>
                                {item.texto}
                              </p>
                              {longo && (
                                <button
                                  type="button"
                                  aria-expanded={expanded}
                                  onClick={() => setExpandedPareceres(prev => ({ ...prev, [item.key]: !expanded }))}
                                  className="mt-2 text-xs font-medium text-blue-600 hover:text-blue-800 underline underline-offset-2"
                                >
                                  {expanded ? 'Ver menos ↑' : 'Ver mais ↓'}
                                </button>
                              )}
                            </div>
                          ) : (
                            <div className="px-4 py-3">
                              <p className="text-sm text-gray-400 italic">Texto do parecer não disponível.</p>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  </>
                );
              })()}
            </div>

          </div>
        )}
      </div>

      {/* ── Inline document viewer (full-screen overlay) ────────────────────── */}
      {viewer && (
        <FileViewerModal
          url={viewer.url}
          nome={viewer.nome}
          mimeType={viewer.mimeType}
          subtitle={
            <>
              {viewer.groupLabel}
              {viewer.globalIndex >= 0 && (
                <> - {displayDocumentCode(viewer.documentCode)} - {viewer.globalIndex + 1} / {combinedDocs.length}</>
              )}
            </>
          }
          onClose={() => setViewer(null)}
          onPrev={() => openViewer(viewer.globalIndex - 1)}
          onNext={() => openViewer(viewer.globalIndex + 1)}
          canPrev={viewer.globalIndex > 0}
          canNext={viewer.globalIndex >= 0 && viewer.globalIndex < combinedDocs.length - 1}
        />
      )}

      {/* ── Parecer assinado ──────────────────────────────────────────────────── */}
      {(isJuridico || isAdmin() || isAuxiliar) && isAtEtapaJuridica && (
        <div className={`${panelClass} p-4`}>
          <h2 className="text-base font-semibold text-slate-950 dark:text-slate-50">Parecer assinado</h2>
          <p className={`mt-0.5 text-xs ${mutedTextClass}`}>
            Anexe o PDF do parecer jurídico após assinatura do procurador. Um novo upload substitui o anterior.
          </p>

          <div className="mt-3">
            {parecerAnexo ? (
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 rounded-lg bg-green-50 border border-green-200">
                <div>
                  <p className="text-sm font-medium text-green-900">{parecerAnexo.nomeOriginal}</p>
                  <p className="text-xs text-green-700 mt-0.5">
                    Enviado por {parecerAnexo.usuarioUpload || 'sistema'} em{' '}
                    {parecerAnexo.dataUpload ? new Date(parecerAnexo.dataUpload).toLocaleString('pt-BR') : '—'}
                  </p>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button
                    type="button"
                    onClick={() => {
                      const url = planoService.parecerAnexoDownloadUrl(id!);
                      setViewer({ url, nome: parecerAnexo.nomeOriginal, mimeType: 'application/pdf', groupLabel: 'Parecer assinado', globalIndex: -1 });
                    }}
                    className="inline-flex items-center px-3 py-1.5 rounded border border-green-300 text-xs font-medium text-green-800 bg-white hover:bg-green-50"
                  >
                    Visualizar
                  </button>
                  <AuthenticatedFileButton
                    action="download"
                    url={planoService.parecerAnexoDownloadUrl(id!)}
                    fileName={parecerAnexo.nomeOriginal}
                    className="inline-flex items-center px-3 py-1.5 rounded border border-green-300 text-xs font-medium text-green-800 bg-white hover:bg-green-50"
                  >
                    Baixar
                  </AuthenticatedFileButton>
                </div>
              </div>
            ) : (
              <p className={`text-sm ${mutedTextClass}`}>Nenhum parecer assinado anexado ainda.</p>
            )}

            <div className="mt-3">
              <input
                ref={parecerInputRef}
                type="file"
                accept="application/pdf"
                className="hidden"
                onChange={handleParecerUpload}
              />
              <button
                type="button"
                disabled={uploadingParecer}
                onClick={() => parecerInputRef.current?.click()}
                className={secondaryButtonClass}
              >
                {uploadingParecer ? 'Enviando...' : parecerAnexo ? 'Substituir parecer assinado (PDF)' : 'Anexar parecer assinado (PDF)'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Conclusão jurídica ────────────────────────────────────────────────── */}
      <div className={`${panelClass} overflow-hidden`}>
        <div className={panelHeaderClass}>
          <h2 className="text-base font-semibold text-slate-950 dark:text-slate-50">Conclusão jurídica</h2>
          <p className={`mt-0.5 text-xs ${mutedTextClass}`}>
            Defina o encaminhamento e, se necessário, registre a fundamentação da análise.
          </p>
        </div>

        <div className="p-4 space-y-5">
          {/* Encaminhamento */}
          <div>
            <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-200">Encaminhamento</label>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {ENCAMINHAMENTOS.map((option) => {
                const active = encaminhamentoFinal === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    disabled={!canEditParecer}
                    onClick={() => setEncaminhamentoFinal(option.value)}
                    className={`flex flex-col text-left p-3 rounded-lg border text-sm transition ${
                      active
                        ? option.activeClass
                        : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800'
                    } disabled:cursor-not-allowed disabled:opacity-60`}
                  >
                    <span className="font-semibold">{option.label}</span>
                    <span className="mt-0.5 text-xs text-slate-600 dark:text-slate-300">{option.description}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Fundamentação */}
          <div>
            <label className={labelClass}>
              Fundamentação jurídica <span className={mutedTextClass}>(opcional)</span>
            </label>
            <textarea
              value={fundamentacao}
              onChange={(e) => setFundamentacao(toUpperText(e.target.value))}
              disabled={!canEditParecer}
              rows={6}
              className={inputClass}
              placeholder="Opcional: descreva o embasamento jurídico, os principais achados da análise e a motivação do encaminhamento."
            />
          </div>

          {/* Identificação do parecer */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className={labelClass}>
                Número do parecer <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={numeroParecer}
                onChange={(e) => setNumeroParecer(toUpperText(e.target.value))}
                disabled={!canEditParecer}
                className={inputClass}
                placeholder="Ex.: 123"
              />
            </div>
            <div>
              <label className={labelClass}>
                Ano do parecer <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                inputMode="numeric"
                value={anoParecer}
                onChange={(e) => setAnoParecer(e.target.value)}
                disabled={!canEditParecer}
                min={2000}
                max={2100}
                className={inputClass}
                placeholder="Ex.: 2026"
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-wrap gap-2 pt-1">
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || deciding || !canEditParecer}
              className={primaryButtonClass}
            >
              {saving ? 'Salvando...' : 'Salvar'}
            </button>
            {isAuxiliar && !isJuridico && !isAdmin() ? (
              <button
                type="button"
                onClick={handleEnviarAprovacao}
                disabled={saving || deciding || !canEditParecer}
                className={successButtonClass}
              >
                {deciding ? 'Enviando...' : 'Salvar e enviar para aprovação'}
              </button>
            ) : (
              <button
                type="button"
                onClick={handleConcluirEtapa}
                disabled={saving || deciding || !canEditConclusao}
                className={successButtonClass}
              >
                {deciding ? 'Concluindo...' : 'Salvar e concluir etapa'}
              </button>
            )}
            {(isAdmin() || (isJuridico && plano?.procuradorJuridicoId != null && String(plano.procuradorJuridicoId) === String(user?.id))) && plano?.status === 'EM_ANALISE' && isAtEtapaJuridica && (
              <button
                type="button"
                onClick={() => setShowRetificacaoModal(true)}
                disabled={saving || deciding}
                className="inline-flex items-center px-4 py-2 rounded border border-amber-400 bg-amber-50 text-amber-800 text-sm font-medium hover:bg-amber-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Solicitar Retificação
              </button>
            )}
            <button
              type="button"
              onClick={handleDownloadZip}
              disabled={downloadingZip}
              className={`${secondaryButtonClass} gap-1.5`}
            >
              <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
              {downloadingZip ? 'Gerando ZIP...' : 'Baixar todos os arquivos'}
            </button>
            <button
              type="button"
              onClick={() => navigate(PROCURADORIA_LIST_ROUTE)}
              className={secondaryButtonClass}
            >
              Voltar para a lista da Procuradoria
            </button>
          </div>

          <p className={`text-xs ${mutedTextClass}`}>
            <strong>Aprovação</strong> e <strong>Aprovação com ressalvas</strong> avançam para a formalização do termo de fomento. <strong>Rejeição</strong> reprova o plano e o encaminha ao Orçamento para devolução da emenda.
          </p>
        </div>
      </div>

      {showRetificacaoModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white shadow-xl p-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Solicitar Retificação</h2>
            <p className="text-sm text-gray-600">
              Descreva o que precisa ser ajustado pela instituição (documentos ou plano de trabalho). O operador receberá a notificação na sua área de trabalho.
            </p>
            <textarea
              value={retificacaoMotivo}
              onChange={(e) => setRetificacaoMotivo(toUpperText(e.target.value))}
              rows={4}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-amber-500 focus:ring-amber-500 sm:text-sm placeholder:text-gray-400"
              placeholder="Ex.: Atualizar o estatuto social vencido e corrigir o objetivo do plano de trabalho."
            />
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => { setShowRetificacaoModal(false); setRetificacaoMotivo(''); }}
                disabled={solicitandoRetificacao}
                className="px-4 py-2 rounded border border-gray-300 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleSolicitarRetificacao}
                disabled={solicitandoRetificacao || !retificacaoMotivo.trim()}
                className="px-4 py-2 rounded bg-amber-600 text-white text-sm font-medium hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {solicitandoRetificacao ? 'Solicitando…' : 'Confirmar Retificação'}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
};

export default PlanoAnaliseJuridicaPage;
